"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoaderCircle, Trash } from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { GetVideoResponseType } from "../../../hooks/useGetVideo";
import { useStorage } from "@/features/storage/useStorage";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@/lib/localization";

interface DeleteVideoButtonProps {
    video: GetVideoResponseType["data"];
}

export default function DeleteVideoButton({ video }: DeleteVideoButtonProps) {
    const setOpen = useStorage((state) => state.setVideoSheetOpen);

    const [isPending, setIsPending] = useState(false);

    const queryClient = useQueryClient();
    const deleteDescription = t("storage.delete-video-modal.header.description", undefined, undefined);
    const [deleteDescriptionBefore, deleteDescriptionAfter] =
        deleteDescription.split("{videoTitle}");

    const closeModal = () => {
        setOpen(false);
        setIsPending(false);
    };

    const submitHandler = async () => {
        setIsPending(true);

        const res = await client.api.storage.video[":videoId"].delete.$delete({
            param: {
                videoId: video.id,
            },
        });

        if (!res.ok) {
            const error = await res.text();
            console.log(error);
            toast.error(t("toast.error.delete-video"), {
                keyName: "toast.error.delete-video",
            });
        } else {
            const data = await res.json();
            console.log(data);

            queryClient.invalidateQueries({ queryKey: ["get-storage-files"] });

            toast.success(t("toast.success.delete-video"), {
                keyName: "toast.success.delete-video",
            });
            closeModal();
        }
    };

    return (
        <AlertDialog>
            <AlertDialogTrigger>
                <div
                    className={buttonVariants({
                        variant: "secondary",
                        size: "sm",
                    })}
                >
                    <Trash className="size-3.5 me-2" />
                    {t("video-sheet.header.delete-btn", undefined, undefined)}
                </div>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:max-w-[380px]">
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {t("storage.delete-video-modal.header.title", undefined, undefined)}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {deleteDescriptionBefore}
                        <span className="font-semibold">
                            {video?.title || ""}
                        </span>
                        {deleteDescriptionAfter || ""}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-row gap-3">
                    <AlertDialogCancel
                        disabled={isPending}
                        className="flex justify-center items-center"
                    >
                        {t("storage.delete-video-modal.cancel-btn", undefined, undefined)}
                    </AlertDialogCancel>
                    <Button
                        disabled={isPending}
                        onClick={() => submitHandler()}
                        variant="destructive"
                        className="flex justify-center items-center"
                    >
                        {isPending ? (
                            <LoaderCircle className="size-4 text-white animate-spin" />
                        ) : (
                            t("storage.delete-video-modal.delete-btn", undefined, undefined)
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
